import React, { useState } from "react";

const PlayerIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <circle cx="8" cy="5.5" r="2.5" opacity="0.9" />
    <path d="M3 13c0-2.1 2.7-3.5 5-3.5S13 10.9 13 13v1H3v-1z" opacity="0.9" />
  </svg>
);

const ClockIcon = () => (
  <svg
    className="h-4 w-4"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="6" />
    <path d="M8 4.5v3.2l2.2 1.3" strokeLinecap="round" />
  </svg>
);

const DifficultyIcon = ({ level }) => {
  const levels = { Easy: 1, Medium: 2, Hard: 3, Expert: 4 };
  const filledBars = levels[level] ?? 0;

  return (
    <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      {[0, 1, 2, 3].map((index) => (
        <rect
          key={index}
          x={index * 3 + 1}
          y={16 - (index + 2) * 2}
          width="2"
          height={(index + 2) * 2}
          rx="1"
          fill="currentColor"
          opacity={index < filledBars ? 0.95 : 0.25}
        />
      ))}
    </svg>
  );
};

const competitionPaths = {
  Coop: (
    <path
      d="M3.5 9.5c1.5 1.4 3 1.4 4.5 0l1-1 2 2 1.5-1.5-2.6-2.6a2.5 2.5 0 1 0-3.3-3.7L9 4.5 6.5 7 5 5.5 3.5 7l1.5 1.5-1.5 1z"
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
      opacity="0.9"
    />
  ),
  Competitive: (
    <path
      d="M8 2l2 4 4.5.7-3.2 3.1.8 4.5L8 12.4 3.9 14.3l.8-4.5-3.2-3.1L6 6l2-4z"
      fill="currentColor"
      opacity="0.9"
    />
  ),
  Mixed: (
    <path
      d="M3 5.5a2.5 2.5 0 1 1 3.9 2l-4 4A2.5 2.5 0 1 1 2 8.5l4-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.9"
    />
  ),
};

const CompetitionIcon = ({ type }) => (
  <svg className="h-4 w-4" viewBox="0 0 16 16" aria-hidden="true">
    {competitionPaths[type] ?? competitionPaths.Mixed}
  </svg>
);

const competitionLabels = {
  Mixed: "Co-op & Comp",
};

const PlayIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M5 3.5v9l7-4.5-7-4.5z" />
  </svg>
);

const LockIcon = () => (
  <svg
    className="h-4 w-4"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <rect x="3.5" y="7" width="9" height="6" rx="1.6" />
    <path d="M5.5 7V5.5a2.5 2.5 0 1 1 5 0V7" strokeLinecap="round" />
  </svg>
);

const StatusBadge = ({ playable }) => {
  const isPlayable = Boolean(playable);
  const toneClasses = isPlayable
    ? "text-emerald-300"
    : "text-red-300";
  const iconRingClasses = isPlayable
    ? "border-emerald-400/80 bg-emerald-400/30"
    : "border-red-400/80 bg-red-400/30";
//   const toneClasses = isPlayable
//     ? "border-emerald-400/70 bg-slate-950/70 text-emerald-300 shadow-[0_0_0_1px_rgba(52,211,153,0.12),0_10px_24px_rgba(16,185,129,0.16)]"
//     : "border-red-400/70 bg-slate-950/70 text-red-300 shadow-[0_0_0_1px_rgba(248,113,113,0.12),0_10px_24px_rgba(239,68,68,0.16)]";
//   const iconRingClasses = isPlayable
//     ? "border-emerald-400/70 bg-emerald-400/10"
//     : "border-red-400/70 bg-red-400/10";

  return (
    <div
      className={`pointer-events-none absolute right-1 top-1 z-20 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 ${toneClasses}`}
    >
      <span className={`grid h-6 w-6 place-items-center rounded-full border ${iconRingClasses}`}>
        {isPlayable ? <PlayIcon /> : <LockIcon />}
      </span>
      
    </div>
  );
};

export default function GameCard({
  title,
  min_players,
  max_players,
  difficulty,
  playtime_estimate,
  competition_type,
  tags,
  cover_image,
  cover_video,
  user_has_access,
  onSelect,
  remoteFocused = false,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const isPlayable = Boolean(user_has_access);

  const handleMouseEnter = () => {
    if (!cover_video) return;
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  const handleKeyDown = (event) => {
    if ((event.key === "Enter" || event.key === " ") && onSelect) {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <article
      data-remote-focused={remoteFocused ? "true" : undefined}
      className={`group relative overflow-hidden rounded-3xl border bg-indigo-950/30 shadow-xl cursor-pointer backdrop-blur transition-transform duration-300 hover:-translate-y-1 hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400 ${remoteFocused ? "-translate-y-1 border-cyan-300 ring-4 ring-cyan-300/25 shadow-cyan-500/30" : "border-white/10"}`}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="button"
      tabIndex={0}
    >
      <StatusBadge playable={isPlayable} />
      <div className="relative h-48 overflow-hidden">
        {/* Cover image */}
        <img
          src={cover_image}
          alt={title}
          className={`h-full w-full object-cover transition-opacity duration-300 ${
            isHovered && cover_video ? "opacity-0" : "opacity-100"
          }`}
        />

        {/* Hover GIF (if provided) */}
        {cover_video ? (
          <img
            src={cover_video}
            alt={`${title} preview`}
            loading="lazy"
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
              isHovered ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-indigo-950 via-indigo-900/40 to-transparent opacity-90" />
      </div>
      <div className="flex flex-col gap-4 px-5 pt-5 pb-6">
        <h3 className="text-lg font-semibold tracking-tight text-white">{title}</h3>
        <dl className="flex flex-row justify-around text-sm text-indigo-100/80">
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-2">
              <dt className="sr-only">Players</dt>
              <PlayerIcon />
              <dd className="font-medium">
                {min_players} - {max_players}
              </dd>
            </div>
            <div className="flex items-center gap-2">
              <dt className="sr-only">Difficulty</dt>
              <DifficultyIcon level={difficulty} />
              <dd className="font-medium">{difficulty}</dd>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-2">
              <dt className="sr-only">Playtime</dt>
              <ClockIcon />
              <dd className="font-medium">
                ~ {playtime_estimate} min
              </dd>
            </div>
            <div className="flex items-center gap-2">
              <dt className="sr-only">Competition Type</dt>
              <CompetitionIcon type={competition_type} />
              <dd className="font-medium">{competitionLabels[competition_type] ?? competition_type}</dd>
            </div>
          </div>
        </dl>
        {tags?.length ? (
          <div className="flex flex-wrap justify-center gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-indigo-500/10 px-3 py-1 text-xs font-medium uppercase tracking-wide text-indigo-100/80"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
}
